import {
  deleteAdminV1OrgSchedulesId,
  deleteAdminV1OrgSchedulesIdMembersProductId,
  getAdminV1OrgProducts,
  getAdminV1OrgSchedules,
  getAdminV1OrgSchedulesId,
  getAdminV1OrgSchedulesIdMembers,
  getAdminV1OrgSchedulesIdRuns,
  getAdminV1OrgSchedulesIdRunsRunId,
  getAdminV1OrgSchedulesIdRunsRunIdItems,
  getAdminV1OrgSchedulesTimeline,
  patchAdminV1OrgSchedulesId,
  patchAdminV1OrgSchedulesIdMembersProductId,
  postAdminV1OrgSchedules,
  postAdminV1OrgSchedulesIdActivate,
  postAdminV1OrgSchedulesIdEnd,
  postAdminV1OrgSchedulesIdMembers,
  postAdminV1OrgSchedulesIdMembersBulk,
  postAdminV1OrgSchedulesIdPause,
  postAdminV1OrgSchedulesIdPreview,
  postAdminV1OrgSchedulesIdResume,
  postAdminV1OrgSchedulesIdRunNow,
  type AddScheduleMembersRequest,
  type BulkResponse,
  type BulkScheduleMembersResponse,
  type CreateProductScheduleRequest,
  type DeleteProductScheduleResponse,
  type GetAdminV1OrgSchedules200,
  type GetAdminV1OrgSchedulesIdMembers200,
  type GetAdminV1OrgSchedulesIdMembersParams,
  type GetAdminV1OrgSchedulesIdRuns200,
  type GetAdminV1OrgSchedulesIdRunsParams,
  type GetAdminV1OrgSchedulesIdRunsRunIdItems200,
  type GetAdminV1OrgSchedulesIdRunsRunIdItemsParams,
  type GetAdminV1OrgSchedulesParams,
  type GetAdminV1OrgSchedulesTimelineParams,
  type ProductListItem,
  type ProductSchedule,
  type ProductScheduleChange,
  type ProductScheduleCreated,
  type RemoveScheduleMemberResponse,
  type ScheduleMemberAction,
  type ScheduleMemberChange,
  type ScheduleOnEndInput,
  type SchedulePreview,
  type ScheduleRun,
  type ScheduleRunNowResponse,
  type ScheduleTimeline,
  type UpdateProductScheduleRequest,
  type UpdateScheduleMemberRequest,
} from '@nucleus-commerce/api-client/admin';

export type ScheduleListParams = GetAdminV1OrgSchedulesParams;
export type MemberListParams = GetAdminV1OrgSchedulesIdMembersParams;
export type RunListParams = GetAdminV1OrgSchedulesIdRunsParams;
export type RunItemListParams = GetAdminV1OrgSchedulesIdRunsRunIdItemsParams;
export type TimelineParams = GetAdminV1OrgSchedulesTimelineParams;

/** What the pool's bulk bar does to every selected member. */
export type MemberBulkAction = ScheduleMemberAction;

export interface BulkMembersRequest {
  productIds: readonly string[];
  action: MemberBulkAction;
}

/** `members/bulk` and `?ids=` take at most 200 ids per call. */
export const BULK_MEMBER_CHUNK = 200;
const IDS_CHUNK = 200;
/** The products list's page cap. */
const PRODUCTS_PAGE = 100;

const chunks = <T>(values: readonly T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(values.length / size) }, (_, i) =>
    values.slice(i * size, i * size + size),
  );

/**
 * Organisation-level product schedules (rotations): CRUD, the lifecycle actions, the pool and the
 * run history. Every write that puts a product on a schedule also creates its draft store product
 * in each target store, so store catalogs change with the pool.
 */
export const schedulesApi = {
  list: (params: ScheduleListParams): Promise<GetAdminV1OrgSchedules200> =>
    getAdminV1OrgSchedules(params),
  get: (id: string): Promise<ProductSchedule> => getAdminV1OrgSchedulesId({ id }),
  /** With a `selection`, products that cannot join are skipped and listed in `memberResult`. */
  create: (body: CreateProductScheduleRequest): Promise<ProductScheduleCreated> =>
    postAdminV1OrgSchedules(body),
  update: (id: string, body: UpdateProductScheduleRequest): Promise<ProductScheduleChange> =>
    patchAdminV1OrgSchedulesId({ id }, body),
  /**
   * 204 by default. With `removeDrafts`, the never-published drafts the schedule created (that
   * no other schedule holds) go with it and the answer counts them.
   */
  remove: (
    id: string,
    { removeDrafts = false }: { removeDrafts?: boolean } = {},
  ): Promise<DeleteProductScheduleResponse | null> =>
    deleteAdminV1OrgSchedulesId({ id }, removeDrafts ? { removeDrafts: true } : undefined).then(
      (answer) => answer ?? null,
    ),
  activate: (id: string): Promise<ProductSchedule> => postAdminV1OrgSchedulesIdActivate({ id }),
  pause: (id: string): Promise<ProductSchedule> => postAdminV1OrgSchedulesIdPause({ id }),
  resume: (id: string): Promise<ProductSchedule> => postAdminV1OrgSchedulesIdResume({ id }),
  end: (id: string, onEnd: ScheduleOnEndInput): Promise<ProductScheduleChange> =>
    postAdminV1OrgSchedulesIdEnd({ id }, { onEnd }),
  /** 202: the run is queued; it shows in the runs list once the worker picks it up. */
  runNow: (id: string): Promise<ScheduleRunNowResponse> => postAdminV1OrgSchedulesIdRunNow({ id }),
  /** A dry run of the next batch: nothing is written. */
  preview: (id: string): Promise<SchedulePreview> => postAdminV1OrgSchedulesIdPreview({ id }),
  members: (id: string, params: MemberListParams): Promise<GetAdminV1OrgSchedulesIdMembers200> =>
    getAdminV1OrgSchedulesIdMembers({ id }, params),
  /** 200 or 207 per id; a product another schedule owns is skipped as a 422 SCHEDULE_CONFLICT item. */
  addMembers: (id: string, body: AddScheduleMembersRequest): Promise<BulkResponse> =>
    postAdminV1OrgSchedulesIdMembers({ id }, body),
  updateMember: (
    id: string,
    productId: string,
    body: UpdateScheduleMemberRequest,
  ): Promise<ScheduleMemberChange> =>
    patchAdminV1OrgSchedulesIdMembersProductId({ id, productId }, body),
  removeMember: (id: string, productId: string): Promise<RemoveScheduleMemberResponse> =>
    deleteAdminV1OrgSchedulesIdMembersProductId({ id, productId }),
  runs: (id: string, params: RunListParams): Promise<GetAdminV1OrgSchedulesIdRuns200> =>
    getAdminV1OrgSchedulesIdRuns({ id }, params),
  run: (id: string, runId: string): Promise<ScheduleRun> =>
    getAdminV1OrgSchedulesIdRunsRunId({ id, runId }),
  runItems: (
    id: string,
    runId: string,
    params: RunItemListParams,
  ): Promise<GetAdminV1OrgSchedulesIdRunsRunIdItems200> =>
    getAdminV1OrgSchedulesIdRunsRunIdItems({ id, runId }, params),
  /**
   * Predicted rotate runs of active schedules and one-shot publish / unpublish dates in a window
   * of at most 92 days; ending release runs are not predicted.
   */
  timeline: (params: TimelineParams): Promise<ScheduleTimeline> =>
    getAdminV1OrgSchedulesTimeline(params),
  /**
   * Master products by id (titles and handles of ids handed over in the URL): `?ids=` 200 at a
   * time, each read to its last page of 100. Unknown ids are simply missing from the answer.
   */
  productsByIds: async (ids: readonly string[]): Promise<ProductListItem[]> => {
    const found: ProductListItem[] = [];
    for (const chunk of chunks(ids, IDS_CHUNK)) {
      let cursor: string | null = null;
      do {
        const page = await getAdminV1OrgProducts({
          ids: chunk.join(','),
          limit: PRODUCTS_PAGE,
          ...(cursor ? { cursor } : {}),
        });
        found.push(...page.items);
        cursor = page.nextCursor;
      } while (cursor);
    }
    return found;
  },
  /**
   * Pin, unpin, exclude, include or remove many members through `members/bulk`, 200 ids a call,
   * merged into one answer (rows in the order sent). `releaseRunId` is the last release run a
   * chunk ran; `remove` on a rule member excludes it instead (`meta.excluded` on its row).
   */
  bulkMembers: async (
    id: string,
    { productIds, action }: BulkMembersRequest,
  ): Promise<BulkScheduleMembersResponse> => {
    const merged: BulkScheduleMembersResponse = {
      succeeded: 0,
      failed: 0,
      items: [],
      releaseRunId: null,
    };
    for (const chunk of chunks(productIds, BULK_MEMBER_CHUNK)) {
      const answer = await postAdminV1OrgSchedulesIdMembersBulk(
        { id },
        { productIds: chunk, action },
      );
      merged.succeeded += answer.succeeded;
      merged.failed += answer.failed;
      merged.items.push(...answer.items);
      merged.releaseRunId = answer.releaseRunId ?? merged.releaseRunId;
    }
    return merged;
  },
};
